// CC03/CC06 · Aplicar en LOTE sobre toda la cartera + bandeja única de
// propuestas por revisar (aceptar/corregir/rechazar con motivo).
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, Wand2, Loader2, Check, Pencil, Ban, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";

const CONF_CLS = {
  exacta: "bg-emerald-100 text-emerald-700",
  derivada: "bg-sky-100 text-sky-700",
  inferida: "bg-amber-100 text-amber-700",
};

function BulkProposalRow({ p, onResolve }) {
  const [mode, setMode] = useState(null); // "reject" | "correct" | null
  const [val, setVal] = useState(p.valor_propuesto);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  const act = async (accion, payload) => {
    setBusy(true);
    try { await onResolve(p.id, { accion, ...payload }); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-2.5 bg-white" data-testid={`bulk-prop-${p.id}`}>
      <div className="flex items-start gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold text-gray-800 truncate">{p.oferta_cliente || "—"}</div>
          <div className="text-[10px] text-gray-400 truncate">{p.oferta_objeto || ""}</div>
          <div className="text-[11px] text-gray-700 mt-1">
            <b>{p.label}:</b> <span className="text-[#007934] font-semibold">{p.valor_propuesto}</span>
            <span className={`ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold ${CONF_CLS[p.confianza] || "bg-gray-100 text-gray-600"}`}>{p.confianza}</span>
          </div>
          <div className="text-[10px] text-gray-400 mt-0.5 italic">{p.fuente}</div>
        </div>
        {!mode && (
          <div className="flex items-center gap-1 shrink-0">
            <button disabled={busy} onClick={() => act("aceptar")} data-testid={`bulk-accept-${p.id}`}
              className="text-[10px] font-semibold bg-[#007934] text-white px-2 py-1 rounded hover:bg-[#006528] disabled:opacity-40 flex items-center gap-1">
              {busy ? <Loader2 className="animate-spin" size={10}/> : <Check size={10}/>} Aceptar
            </button>
            <button disabled={busy} onClick={() => { setMode("correct"); setVal(p.valor_propuesto); }} data-testid={`bulk-correct-${p.id}`}
              className="text-[10px] font-semibold border border-gray-300 text-gray-600 px-2 py-1 rounded hover:bg-gray-50 flex items-center gap-1">
              <Pencil size={10}/> Corregir
            </button>
            <button disabled={busy} onClick={() => { setMode("reject"); setMotivo(""); }} data-testid={`bulk-reject-${p.id}`}
              className="text-[10px] font-semibold border border-rose-300 text-rose-600 px-2 py-1 rounded hover:bg-rose-50 flex items-center gap-1">
              <Ban size={10}/> Rechazar
            </button>
          </div>
        )}
      </div>
      {mode === "correct" && (
        <div className="flex items-center gap-1 mt-2">
          <input value={val} onChange={e => setVal(e.target.value)} data-testid={`bulk-correct-input-${p.id}`}
            className="flex-1 border border-gray-200 rounded px-2 py-1 text-[11px]"/>
          <button disabled={busy || !val.trim()} onClick={() => act("corregir", { valor_corregido: val.trim() })}
            data-testid={`bulk-correct-confirm-${p.id}`}
            className="text-[10px] font-semibold bg-[#007934] text-white px-2 py-1 rounded disabled:opacity-40">Guardar</button>
          <button onClick={() => setMode(null)} className="text-[10px] text-gray-400 px-1">✕</button>
        </div>
      )}
      {mode === "reject" && (
        <div className="flex items-center gap-1 mt-2">
          <input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Motivo del rechazo…" data-testid={`bulk-reject-input-${p.id}`}
            className="flex-1 border border-gray-200 rounded px-2 py-1 text-[11px]"/>
          <button disabled={busy || !motivo.trim()} onClick={() => act("rechazar", { motivo: motivo.trim() })}
            data-testid={`bulk-reject-confirm-${p.id}`}
            className="text-[10px] font-semibold bg-rose-600 text-white px-2 py-1 rounded disabled:opacity-40">Rechazar</button>
          <button onClick={() => setMode(null)} className="text-[10px] text-gray-400 px-1">✕</button>
        </div>
      )}
    </div>
  );
}

export default function CompletitudBulkModal({ onClose }) {
  const [props, setProps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/ofertas-comercial/completitud/proposals-all`)
      .then(r => setProps(r.data.proposals || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const runBulk = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/ofertas-comercial/completitud/run-bulk`);
      setSummary(r.data);
      const by = Object.entries(r.data.applied_by_field || {}).map(([k, v]) => `${k}: ${v}`).join(", ");
      toast.success(`Aplicados ${r.data.applied_total} datos exactos${by ? ` (${by})` : ""} · ${r.data.proposals_created} propuestas nuevas`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al ejecutar en lote");
    } finally { setRunning(false); }
  };

  const resolve = async (pid, payload) => {
    try {
      await axios.post(`${API}/ofertas-comercial/completitud/proposals/${pid}/resolve`, payload);
      setProps(prev => prev.filter(p => p.id !== pid));
      toast.success(payload.accion === "rechazar" ? "Propuesta rechazada" : "Propuesta aplicada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo resolver");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="completitud-bulk-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full my-8 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between sticky top-0 bg-white rounded-t-xl z-10">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
              <ShieldCheck size={20} className="text-sky-700"/>
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Completar cartera (en lote)</h3>
              <p className="text-xs text-gray-500 mt-0.5">Auto-aplica los datos deterministas y reúne las propuestas por revisar.</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="bulk-close-btn" className="text-gray-400 hover:text-gray-600"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-4">
          <button onClick={runBulk} disabled={running} data-testid="bulk-run-btn"
            className="w-full bg-[#007934] text-white text-xs font-semibold px-4 py-2.5 rounded-lg hover:bg-[#006528] disabled:opacity-40 flex items-center justify-center gap-2">
            {running ? <Loader2 className="animate-spin" size={14}/> : <Wand2 size={14}/>}
            {running ? "Ejecutando sobre toda la cartera…" : "Ejecutar sobre toda la cartera"}
          </button>

          {summary && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-[11px] text-emerald-800" data-testid="bulk-summary">
              Revisadas <b>{summary.offers_scanned}</b> ofertas · <b>{summary.applied_total}</b> datos exactos auto-aplicados
              {Object.keys(summary.applied_by_field || {}).length > 0 && (
                <span> ({Object.entries(summary.applied_by_field).map(([k, v]) => `${k}: ${v}`).join(", ")})</span>
              )} · <b>{summary.proposals_created}</b> propuestas nuevas · <b>{summary.pending_total}</b> pendientes de revisar.
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide mb-2">
              Propuestas por revisar ({props.length})
            </div>
            {loading ? (
              <div className="text-xs text-gray-400 py-6 text-center"><Loader2 className="animate-spin inline mr-2" size={14}/>Cargando…</div>
            ) : props.length === 0 ? (
              <div className="text-xs text-gray-400 py-6 text-center" data-testid="bulk-empty">
                No hay propuestas pendientes. Pulsa «Ejecutar sobre toda la cartera» para buscar datos que faltan.
              </div>
            ) : (
              <div className="space-y-2 max-h-[48vh] overflow-y-auto" data-testid="bulk-proposals-list">
                {props.map(p => <BulkProposalRow key={p.id} p={p} onResolve={resolve}/>)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
